import { Button, IconButton } from '@chakra-ui/react'
import { faCommentAlt } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useRouter } from 'next/router'
import type { ReactElement } from 'react'

import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'
import { getConversationUrl } from '@/client/services/url'
import { canUsersChat, hasUserRole } from '@/client/services/user'
import type { UserAuthRole } from '@/client/services/userRole/types'

import RequireLoginAndApproval from '@/components/Auth/RequireLoginAndApproval'
import Tippy from '@/components/UI/Tippy'

export default function UserSendMessageButton({
  userId,
  userRoles,
  onlyIcon = false,
  disabledMessage = null,
  customText,
}: {
  userId: number | null
  userRoles: UserAuthRole[]
  onlyIcon?: boolean
  disabledMessage?: string | null
  customText?: string
}): ReactElement | null {
  const user = useUser()
  const router = useRouter()

  const [handleSendMessageCallback, isSendingMessage] = useAPIWrapper(
    async () => {
      if (userId == null) {
        throw new Error('No user found')
      }

      const conversation = await RPC.startDirectConversation({
        targetUserId: userId,
      })

      await router.push(getConversationUrl(conversation.external_uuid))
    },
    [userId, router],
    'Start direct conversation'
  )

  if (userId === user.id) {
    // Cannot send a message to self
    return null
  }

  if (!canUsersChat(user, { roles: userRoles })) {
    return null
  }

  if (disabledMessage == null && !hasUserRole({ roles: userRoles }, 'user:approved')) {
    disabledMessage = 'You cannot chat with this user right now. Please check back later.'
  }

  return onlyIcon ? (
    <Tippy content={disabledMessage ?? 'Send this person a message!'}>
      <RequireLoginAndApproval>
        <IconButton
          aria-label="Send this person a direct message"
          onClick={handleSendMessageCallback}
          icon={<FontAwesomeIcon icon={faCommentAlt} />}
          isRound
          variant="icon"
          isLoading={isSendingMessage}
          isDisabled={disabledMessage != null}
        />
      </RequireLoginAndApproval>
    </Tippy>
  ) : (
    <Tippy disabled={disabledMessage == null} content={disabledMessage}>
      <RequireLoginAndApproval>
        <Button
          width="auto"
          variant="outline"
          onClick={handleSendMessageCallback}
          leftIcon={<FontAwesomeIcon icon={faCommentAlt} />}
          isLoading={isSendingMessage}
          isDisabled={disabledMessage != null}
        >
          {customText ?? 'Chat'}
        </Button>
      </RequireLoginAndApproval>
    </Tippy>
  )
}
